import { uniq } from 'lodash';

import {
  Cell,
  Exploration,
  ExplorationLayout,
  ExplorationType,
  explorationTypes,
} from '@/explore/types';
import { notNil } from '@/lib/utils';

import {
  isConversationPart,
  deleteCell,
  addCellsAt,
  addCells,
  getCellIndex,
  getCell,
} from '../cell';

export * from './builder';
export * from './conversation';
export * from './dependencies';
export * from './canvas';

export const isModelExploration = (exploration: Exploration) =>
  getModelIdFromModelExploration(exploration) !== undefined ||
  getExplorationType(exploration) === 'model';

export const isEmptyExploration = (exploration: Exploration) =>
  exploration === undefined || exploration.view.cells.length === 0;

const getModelIdFromModelExploration = (exploration: Exploration) =>
  exploration.options?.explorationForModelId;

const getExplorationSection = (exploration: Exploration) => exploration.labels.section;

export const hasExplorationSection = (exploration: Exploration) =>
  notNil(getExplorationSection(exploration));

export const getExplorationSections = (explorations: Exploration[]) =>
  uniq(explorations.map(getExplorationSection).filter(notNil)).sort();

export const getExplorationCell = (exploration: Exploration, cellId: string) =>
  getCell(exploration.view.cells, cellId);

export const getExplorationCellIndex = (exploration: Exploration, cellId: string) =>
  getCellIndex(exploration.view.cells, cellId);

export const addExplorationCells = (exploration: Exploration, cells: Cell[], afterCellId: string) =>
  setExplorationCells(exploration, addCells(exploration.view.cells, cells, afterCellId));

export const addExplorationCell = (exploration: Exploration, cell: Cell, afterCellId: string) =>
  addExplorationCells(exploration, [cell], afterCellId);

export const addExplorationCellsAt = (exploration: Exploration, cells: Cell[], index = 0) =>
  setExplorationCells(exploration, addCellsAt(exploration.view.cells, cells, index));

export const addExplorationCellAt = (exploration: Exploration, cell: Cell, index = 0) =>
  addExplorationCellsAt(exploration, [cell], index);

export const setExplorationCells = (exploration: Exploration, cells: Cell[]): Exploration => ({
  ...exploration,
  view: {
    ...exploration.view,
    cells,
  },
});

export const deleteExplorationCell = (exploration: Exploration, cellId: string): Exploration =>
  setExplorationCells(exploration, deleteCell(exploration.view.cells, cellId));

export const removeExplorationConversation = (exploration: Exploration, conversationId: string) =>
  setExplorationCells(
    exploration,
    exploration.view.cells.filter(
      (c) => !isConversationPart(c) || c.conversationId !== conversationId,
    ),
  );

export const getExplorationType = (exploration: Exploration): ExplorationType =>
  explorationTypes.find((type) => exploration.labels.type === type) ?? null;

export const getExplorationLayout = (exploration: Exploration): ExplorationLayout =>
  exploration.view.options?.layout === 'canvas' ? 'canvas' : 'notebook';

export const setExplorationLayout = (exploration: Exploration, layout: ExplorationLayout) => ({
  ...exploration,
  view: {
    ...exploration.view,
    options: {
      ...exploration.view.options,
      layout,
    },
  },
});

// Explorations can be grouped by prefixing the name with `(<folderName>)`
const ExplorationGroupPattern = /^\([\w\s]+\)\s+/i;

export const getExplorationFolder = (name: string) => {
  return name.match(ExplorationGroupPattern)?.[0].trim().slice(1, -1);
};

export const getExplorationListFolders = (list: { name: string }[]) => {
  const folders = new Set<string>();
  list.forEach((item) => {
    const folder = getExplorationFolder(item.name);
    if (notNil(folder)) {
      folders.add(folder);
    }
  });
  return [...folders];
};

export const explorationHasFolderPrefix = (exploration: { name: string }) => {
  const group = getExplorationFolder(exploration.name);
  return group !== undefined && group.length !== 0;
};

export const getCleanedExplorationName = (name: string) =>
  name.replace(getExplorationFolder(name) ?? '', '').replace('() ', '');

export const mergeExplorations = (base: Exploration, ...rest: Exploration[]): Exploration => {
  const cells = rest.reduce(
    (acc, exploration) => {
      acc.push(...exploration.view.cells);
      return acc;
    },
    [...base.view.cells],
  );

  return {
    ...base,
    view: {
      ...base.view,
      cells,
    },
  };
};
